import { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Icons, Logo } from "@/assets";
import { Button } from "../ui";
import { cx } from "@/utils/cx";
import { createRipple } from "@/utils/ripple";
import { COOKIE_NAMES } from "@/constants/auth";
import { eraseCookie } from "@/utils/cookies";
import { Toast } from "@/utils/toast";
import { ROUTES } from "@/constants/routes";
import { SearchBar } from "../common/SearchBar";
import { NotificationIcon } from "@/assets/icons/notification";
import { ChatIcon } from "@/assets/icons/chat";
import { useMutateLogout } from "@/hooks/auth/useMutateLogout";
import { getStoredUserName, STORAGE_KEYS } from "@/utils/sessionStorageKeys";
import { getCookie } from "@/utils/cookies";
import { getUserIdFromToken } from "@/utils/authUtils";

// --- Menu Configuration ---
const PROFILE_MENU = [
    {
        icon: Icons.UserEditIcon, // Ensure this exists in your Icons object
        label: "Profile",
        subLabel: "Change Profile Picture and change username",
        path: ROUTES.BOTTOM.SETTINGS,
    },
    {
        icon: Icons.NotificationIcon,
        label: "Notifications",
        subLabel: "Status bar",
        path: "/notifications",
    },
    {
        icon: Icons.DocumentIcon,
        label: "Documents",
        subLabel: "Uploaded Documents",
        path: "/documents",
    },
    {
        icon: Icons.SettingIcon,
        label: "Settings",
        subLabel: "Account Setting",
        path: ROUTES.BOTTOM.SETTINGS,
    },
];

export const Topbar = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const isProfilePage = location.pathname === ROUTES.BOTTOM.SETTINGS;
    const isDashboardPage = location.pathname === ROUTES.MAIN.DASHBOARD;
    const [isProfileOpen, setIsProfileOpen] = useState(false);
    const profileRef = useRef<HTMLDivElement>(null);
    const userId = getUserIdFromToken(getCookie(COOKIE_NAMES.ACCESS_TOKEN)) || "-";

    // Mock User Data
    const [user, setUser] = useState({
        name: getStoredUserName(),
        id: userId,
        avatarUrl: "https://i.pravatar.cc/150?u=pavan",
    });

    useEffect(() => {
        const storedName = localStorage.getItem(STORAGE_KEYS.USER_NAME);
        setUser(prev => ({ ...prev, name: storedName || getStoredUserName(), id: userId }));
    }, [userId]);

    // Close popup on outside click
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (profileRef.current && !profileRef.current.contains(event.target as Node)) {
                setIsProfileOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleNavigate = (path: string) => {
        navigate(path);
        setIsProfileOpen(false);
    };

    const { mutate: logout } = useMutateLogout();

    const handleLogout = async (e: React.MouseEvent<HTMLDivElement>) => {
        createRipple(e);

        try {
            logout();
        } catch (error) {
            console.error("Logout failed:", error);
            Toast.error("Logout failed. Please try again.");
        }
    };

    return (
        <header className="w-full h-[86px] bg-white border-b border-[#ebebf0] flex items-center justify-between px-[30px] py-2.5 z-50 sticky top-0">
            <Logo.PaySftPlainLogo />

            {/* Right side items */}
            <div className="flex items-center gap-[20px]">
                {/* Search Bar, Chat & Notification Icons - only on dashboard */}
                {/* {isProfilePage && (
                    <>
                        <SearchBar className="w-[300px] " />
                        <ChatIcon />
                        <NotificationIcon />
                    </>
                )} */}

                {/* Subscription Button - hidden on profile page */}
                {/* {!isProfilePage && (
                    <Button
                        variant="primary"
                        leftIcon={<Icons.CrownIcon />}
                        className="text-sm"
                        onClick={() => navigate('/subscription')}>

                        Subscription
                    </Button>
                )} */}

                {/* Profile Section Wrapper */}
                <div ref={profileRef} className="relative">
                    {/* Profile Trigger (The Pill) */}
                    <div
                        onClick={() => setIsProfileOpen(!isProfileOpen)}
                        className="w-[195px] h-[64px] rounded-full hover:bg-gray-30 cursor-pointer transition-colors flex items-center p-2.5 border border-transparent hover:border-gray-200"
                    >
                        <div className="flex items-center justify-between w-full px-2">
                            <div className="flex items-center gap-2.5">
                                <div className="w-[44px] h-[44px] rounded-full overflow-hidden bg-gray-300 border border-white shadow-sm">
                                    <img src={user.avatarUrl} alt="User" className="w-full h-full object-cover" />
                                </div>
                                <div className="flex flex-col">
                                    <span className="text-base font-semibold text-black leading-none">{user.name}</span>
                                    <span
                                        className="text-[11px] font-medium text-gray-50"
                                        title={user.id}
                                    >
                                        ID {user.id.slice(0, 4)}...{user.id.slice(-4)}
                                    </span>
                                </div>
                            </div>
                            <Icons.ChevronDownIcon
                                className={`w-4 h-4 text-brand transition-transform duration-200 ${isProfileOpen ? 'rotate-180' : ''}`}
                            />
                        </div>
                    </div>

                    {/* --- Profile Popup --- */}
                    {isProfileOpen && (
                        <div className="absolute top-full right-0 mt-2 w-[375px] bg-white rounded-[24px] shadow-2xl border border-white-100 z-50 animate-in fade-in zoom-in-95 duration-200">
                            <div className="p-6 flex flex-col h-full">


                                {/* Header: Avatar & Name */}
                                <div className="flex items-center justify-center flex-row gap-2 mb-6">
                                    <div className="relative w-16 h-16 rounded-full overflow-hidden border-2 border-white shadow-sm">
                                        <img src={user.avatarUrl} alt={user.name} className="w-full h-full object-cover" />
                                    </div>
                                    <div className="text-center">
                                        <h3 className="text-base font-semibold text-black text-sf-pro">{user.name}</h3>
                                        <p className="text-xs text-gray-50 font-medium" title={user.id}>
                                            ID {user.id.slice(0, 4)}...{user.id.slice(-4)}
                                        </p>
                                    </div>
                                </div>

                                {/* Section 1: Menu Items */}
                                {/* <div className="flex flex-col divide-y divide-gray-20">
                                    {PROFILE_MENU.map((item, index) => (
                                        <div
                                            key={index}
                                            onClick={() => handleNavigate(item.path)}
                                            className="group flex items-center gap-4 py-2.5 px-1 cursor-pointer transition-colors hover:bg-gray-10"
                                        >
                                            <div className="w-10 h-10 flex items-center justify-center text-gray-700 group-hover:text-blue-600 transition-colors">
                                                <item.icon className="w-6 h-6 stroke-[1.5]" />
                                            </div>
                                            <div className="flex-1 flex flex-col">
                                                <span className="text-base font-semibold text-gray-110 group-hover:text-blue-600 transition-colors text-sf-pro">
                                                    {item.label}
                                                </span>
                                                <span className="text-xs text-gray-70 font-regular">
                                                    {item.subLabel}
                                                </span>
                                            </div>
                                            <Icons.ChevronRightIcon className="w-5 h-5 text-gray-300 group-hover:text-blue-500" />
                                        </div>
                                    ))}
                                </div> */}

                                {/* Spacer - creates the large gap between sections */}
                                <div className="flex-1" />

                                {/* Section 2: Bottom Actions */}
                                <div className="flex flex-col gap-3">
                                    {/* Remove Account */}
                                    {/* <div className="group flex items-center justify-between px-1 cursor-pointer">
                                        <div className="flex items-center gap-4">
                                            <div className="w-10 h-10 flex items-center justify-center text-gray-500 group-hover:text-red-500">
                                                <Icons.UserCrossIcon className="w-6 h-6 stroke-[1.5]" />
                                            </div>
                                            <div className="flex flex-col">
                                                <span className="text-base font-semibold text-gray-500 group-hover:text-red-500 transition-colors text-sf-pro">
                                                    Request Remove Account
                                                </span>
                                                <span className="text-xs text-gray-50 font-regular text-sf-pro group-hover:text-red-60">
                                                    Delete account
                                                </span>
                                            </div>
                                        </div>
                                        <Icons.TrashIcon className="w-5 h-5 text-gray-300 group-hover:text-red-500 transition-colors" />
                                    </div> */}

                                    {/* Logout */}
                                    <div
                                        onClick={handleLogout}
                                        className="flex items-center gap-4 p-4 rounded-[16px] border border-ultramarine-30 cursor-pointer hover:bg-gray-10 hover:border-ultramarine-50 transition-all group"
                                    >
                                        <div className="w-10 h-10 flex items-center justify-center">
                                            <Icons.LogoutIcon className="w-6 h-6 text-gray-900 group-hover:text-red-600" />
                                        </div>
                                        <div className="flex flex-col">
                                            <span className="text-base font-semibold text-gray-90 group-hover:text-red-600 text-sf-pro">Logout</span>
                                            <span className="text-xs text-gray-40 group-hover:text-red-400 text-sf-pro font-regular">Logout of your account</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </header>
    );
};
